import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Table } from "react-bootstrap";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function CsAndItTable25({ data }) {
  const pdfRef = useRef();

  if (!data?.length) {
    return <p className="text-red-600 text-center">⚠ No data available</p>;
  }

  const races = data.map((d) => d.race_label);
  const emp2019 = data.map((d) => Number(d.emp_2019));
  const emp2020 = data.map((d) => Number(d.emp_2020));
  const pandemicChange = data.map((d) => Number(d.pandemic_change));

  const chartData = {
    labels: races,
    datasets: [
      {
        label: "Employment in 2019",
        data: emp2019,
        backgroundColor: "#FFF085",
        borderRadius: 6,
      },
      {
        label: "Employment in 2020",
        data: emp2020,
        backgroundColor: "#82181A",
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    indexAxis: "y",
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Employment Change Due to the Pandemic (2019-2020)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Employment Count", font: { size: 14, weight: "bold" } },
        ticks: {
          callback: (v) => v.toLocaleString("en-US"),
        },
      },
      y: { title: { display: true, text: "Race", font: { size: 14, weight: "bold" } } },
    },
  };

  // 🔍 Insight Cards Calculation
  const total2019 = emp2019.reduce((a, b) => a + b, 0);
  const total2020 = emp2020.reduce((a, b) => a + b, 0);
  const declinePercent = (((total2020 - total2019) / total2019) * 100).toFixed(
    1
  );

  const mostAffected = data.reduce(
    (min, d) => (d.pandemic_change < min.pandemic_change ? d : min),
    data[0]
  );

  const leastAffected = data.reduce(
    (max, d) => (d.pandemic_change > max.pandemic_change ? d : max),
    data[0]
  );

  // 📄 PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("pandemic_emp_impact.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white shadow-lg rounded-xl">
        <h2 className="text-2xl font-bold text-center mb-6">
          Employment Impact During the Pandemic (2019–2020)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Workforce Drop
            </p>
            <p className="text-xl font-bold text-blue-800">{declinePercent}%</p>
          </div>
          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Most Affected Group
            </p>
            <p className="text-xl font-bold text-red-700">
              {mostAffected.race_label}
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Least Affected Group
            </p>
            <p className="text-xl font-bold text-green-800">
              {leastAffected.race_label}
            </p>
          </div>
        </div>

        {/* Table + Chart Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Race</th>
                    <th className="p-2" style={{ textAlign: 'right' }}>2019</th>
                    <th className="p-2" style={{ textAlign: 'right' }}>2020</th>
                    <th className="p-2" style={{ textAlign: 'right' }}>Change</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-100">
                      <td className="p-2 font-semibold text-left">{row.race_label}</td>
                      <td className="p-2 text-blue-700 font-bold" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {Number(row.emp_2019).toLocaleString("en-US")}
                      </td>
                      <td className="p-2 text-red-700 font-bold" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {Number(row.emp_2020).toLocaleString("en-US")}
                      </td>
                      <td
                        className={`p-2 font-bold ${
                          row.pandemic_change >= 0
                            ? "text-green-700"
                            : "text-red-600"
                        }`}
                        style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {row.pandemic_change.toLocaleString("en-US")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          Download as PDF
          <img src={pdf} alt="pdf" className="w-[1.5rem]" />
        </button>
      </div>
    </>
  );
}
